/**
 * Campaign delivery: the routing tiers, and what actually happened at each.
 *
 * The previous version of this screen was an accordion of groups. That is a
 * list, and a list hides the one thing that matters about route groups: they
 * are *ordered tiers with fall-through*. A lead enters the first tier, and if
 * nothing there takes it — filtered, capped, rejected — it drops to the next.
 * Rendering them as a flat list means the reader has to already know the model
 * to understand the screen.
 *
 * So tiers are stacked in priority order with the fall-through drawn between
 * them, and each tier's destinations are laid out according to that group's
 * distribution method, because "priority", "round-robin" and "weighted" mean
 * three genuinely different things about which destination gets tried.
 *
 * The counts come from `leads.distribution_data.attempts`, not the
 * `group_attempts` table. That table records only successes for the live
 * campaign — one group, `accepted` only — so it cannot answer "why did this
 * lead not place", which is the entire question an operator has.
 *
 * An assignment tier (a team's rotation) is drawn last whatever its
 * priority, because that is when the engine runs it: after every selling
 * tier, for leads nobody bought. It never sells, so it gets its own card.
 *
 * Price and caps on the selling tiers show only on a stream that sells
 * (`money`); a stream that only sends or assigns has no money to show.
 */
import { useState } from 'react';
import {
  ActionIcon, Alert, Badge, Button, Card, Code, Group, Loader, Progress,
  Stack, Table, Text, Tooltip,
} from '@mantine/core';
import { IconAlertTriangle, IconArrowDown, IconPencil, IconPlus, IconUsersGroup } from '@tabler/icons-react';
import { useCampaignDetail, useCampaignRouting, type RoutingRange } from '../api/queries';
import { EditGroupModal } from './EditGroupModal';
import { EditDestinationModal } from './EditDestinationModal';
import { LinkDestinationModal, NewGroupModal } from './RoutingActions';
import { TOKENS, TYPE, formatGBP, formatNumber } from '../theme';
import { CHART_POSITIVE, MONO_SCALE } from '../charts';
import { LegendItem, MonoLabel, MonoSegmented, ProportionList, type ProportionItem } from '../ui';
import { formatShare } from '../ui/format';
import { useVocab, vocab } from '../vocab';
import { AssignmentTierCard, NewRotationModal } from './assignment/Rotations';
import { useCrmPermissions } from './crm/crmShared';

/** Figures in the dashboard's mono, so columns of numbers line up. */
const CHART_INK_COLOUR = TOKENS.text;

const FIG = { ...TYPE.monoFigure, color: TOKENS.text } as const;

type CampaignDetail = NonNullable<ReturnType<typeof useCampaignDetail>['data']>;
type RouteGroup = CampaignDetail['groups'][number];
type GroupDestination = RouteGroup['destinations'][number];
type Routing = NonNullable<ReturnType<typeof useCampaignRouting>['data']>;
type GroupOutcomes = Routing['groups'][number];

export type Price = {
  resolved: number;
  source: string;
  link_value: number;
  destination_value?: number | null;
  buyer_settings_value?: number | null;
};

function sourceLabel(source: string): string | undefined {
  const v = vocab();
  const labels: Record<string, string> = {
    link: 'overridden on this link',
    destination: `the ${v.one('destination')}'s own default`,
    buyer_settings: `falling back to the ${v.one('buyer')}'s settings`,
    none: 'no price set anywhere',
  };
  return labels[source];
}

/** How each outcome should read, and whether it counts as a placement. */
function outcomeLabels(): Record<string, { label: string; colour: string }> {
  const v = vocab();
  return {
    accepted: { label: 'Accepted', colour: 'chartGreen' },
    delivered: { label: 'Delivered', colour: 'chartGreen' },
    rejected: { label: `Rejected by the ${v.one('buyer')}`, colour: 'chartRed' },
    error: { label: 'Delivery error', colour: 'chartRed' },
    timeout: { label: 'Timed out', colour: 'chartRed' },
    group_filtered: { label: `Filtered at the ${v.one('routeGroup')}`, colour: 'chartAmber' },
    filtered: { label: `Filtered at the ${v.one('destination')}`, colour: 'chartAmber' },
    group_capped: { label: `${v.One('routeGroup')} cap reached`, colour: 'gray' },
    capped: { label: `${v.One('destination')} cap reached`, colour: 'gray' },
    duplicate: { label: 'Duplicate', colour: 'gray' },
    suppressed: { label: 'Suppressed', colour: 'gray' },
    suppression_error: { label: 'Held back: suppression not checked', colour: 'chartAmber' },
    cap_error: { label: 'Cap not checked', colour: 'chartAmber' },
    inactive_day: { label: 'Not an active day', colour: 'gray' },
    no_routes: { label: `No eligible ${v.one('destination')}`, colour: 'gray' },
    // The engine never sells one lead to the same buyer twice.
    skipped: { label: `Already ${v.p.sold} to this ${v.one('buyer')}`, colour: 'gray' },
    validation_failed: { label: 'Failed phone check', colour: 'chartAmber' },
  };
}

function outcomeLabel(result: string) {
  return outcomeLabels()[result] ?? { label: result || 'Unknown', colour: 'gray' };
}

export function PriceCell({ price }: { price: Price }) {
  const legacyFallback = price.source === 'buyer_settings';
  const v = useVocab();
  return (
    <Group gap={6} justify="flex-end" wrap="nowrap">
      <Tooltip
        withArrow
        multiline
        w={280}
        label={
          `${formatGBP(price.resolved)}, ${sourceLabel(price.source) ?? price.source}. ` +
          `Link override: ${price.link_value ? formatGBP(price.link_value) : 'none'}. ` +
          `${v.One('destination')} default: ${price.destination_value ? formatGBP(price.destination_value) : 'none'}. ` +
          `${v.One('buyer')} settings: ${price.buyer_settings_value != null ? formatGBP(price.buyer_settings_value) : 'unset'}.`
        }
      >
        <span style={{ ...FIG, fontWeight: 600, cursor: 'help' }}>
          {price.source === 'none' ? '—' : formatGBP(price.resolved)}
        </span>
      </Tooltip>
      {price.source === 'destination' && (
        <Tooltip label={`Inherited from the ${v.one('destination')}'s default; no override on this link`}>
          <Badge size="xs" variant="light" color="gray" style={{ cursor: 'help' }}>default</Badge>
        </Tooltip>
      )}
      {legacyFallback && (
        <Tooltip label={`No price on the link or the ${v.one('destination')}, so this comes from the ${v.one('buyer')}'s settings blob, a legacy home being retired`}>
          <Badge size="xs" variant="light" color="chartAmber" style={{ cursor: 'help' }}>legacy</Badge>
        </Tooltip>
      )}
    </Group>
  );
}

export function CapCell({ used, cap }: { used: number; cap: number }) {
  if (!cap) return <Text size="sm" c="dimmed">none</Text>;
  const pct = Math.min(100, (used / cap) * 100);
  return (
    <Stack gap={4} miw={90}>
      <span style={{ ...FIG, fontSize: 11.5 }}>{formatNumber(used)} / {formatNumber(cap)}</span>
      <Progress
        value={pct}
        size={4}
        radius={4}
        bg={TOKENS.surface2}
        color={pct >= 100 ? TOKENS.red : pct >= 80 ? 'chartAmber.5' : CHART_POSITIVE}
      />
    </Stack>
  );
}

/**
 * How this group picks a destination, said in words.
 *
 * The value is served by the engine (`distribution_method`), so an unknown one
 * renders as itself rather than being silently mapped to a default — a client
 * that quietly treats an unrecognised method as "priority" is exactly the
 * capability drift the served-enums work exists to stop.
 */
function methodBlurb(method: string, count: number) {
  const v = vocab();
  switch (method) {
    case 'priority':
      return `Tried in order, top first, until one accepts.`;
    case 'round-robin':
      return `Cycles through all ${count}, starting from wherever the last ${v.one('lead')} left off.`;
    case 'weighted':
      return `Chosen at random, in proportion to each ${v.one('destination')}'s weight.`;
    default:
      return `Distribution method: ${method}.`;
  }
}

function TierStats({ stats }: { stats: GroupOutcomes | undefined }) {
  if (!stats) {
    return <Text size="xs" c="dimmed">No attempts recorded in this period.</Text>;
  }
  if (stats.attempts === 0) {
    return <Text size="xs" c="dimmed">Nothing reached this tier in this period.</Text>;
  }
  const placed = stats.accepted / stats.attempts;
  return (
    <Stack gap={8}>
      <Group gap="md" wrap="wrap">
        <LegendItem color={TOKENS.borderStrong} label="Reached" value={formatNumber(stats.attempts)} />
        <LegendItem color={CHART_POSITIVE} label="Placed" value={formatNumber(stats.accepted)} />
        <LegendItem color={MONO_SCALE[3]} label="Moved on" value={formatNumber(stats.turned_away)} />
      </Group>
      {/* One bar, green for the share placed here: the dashboard's meter. */}
      <Tooltip label={`${formatNumber(stats.accepted)} of ${formatNumber(stats.attempts)} placed here (${formatShare(stats.accepted, stats.attempts)})`}>
        <div style={{ height: 6, borderRadius: 6, background: TOKENS.surface2, overflow: 'hidden' }}>
          <div
            className="hf-bar"
            style={{ height: '100%', borderRadius: 6, background: CHART_POSITIVE, transform: `scaleX(${placed})` }}
          />
        </div>
      </Tooltip>
    </Stack>
  );
}

/**
 * Outcomes that are not a placement, as proportion rows, largest first. This
 * is the "what should I look at" list, so it is ordered by cost, not by name.
 */
export function lossItems(outcomes: { result: string; count: number }[]): ProportionItem[] {
  const losses = outcomes
    .filter((o) => o.result !== 'accepted' && o.result !== 'delivered' && o.count > 0)
    .sort((a, b) => b.count - a.count);
  const total = losses.reduce((n, o) => n + o.count, 0);
  return losses.map((o, i) => ({
    id: o.result,
    label: outcomeLabel(o.result).label,
    value: formatNumber(o.count),
    share: formatShare(o.count, total),
    fraction: total > 0 ? o.count / total : 0,
    color: MONO_SCALE[Math.min(i + 1, MONO_SCALE.length - 1)],
  }));
}

function TierReasons({ stats }: { stats: GroupOutcomes | undefined }) {
  const v = useVocab();
  if (!stats || stats.turned_away === 0) return null;

  return (
    <Stack gap="sm" mt="md">
      <MonoLabel>{`Why ${v.many('lead')} did not place here`}</MonoLabel>
      <div style={{ maxWidth: 520 }}>
        <ProportionList items={lossItems(stats.outcomes)} />
      </div>
      {stats.reasons.length > 0 && (
        <Table withRowBorders={false} verticalSpacing={4}>
          <Table.Tbody>
            {stats.reasons.slice(0, 6).map((r) => (
              <Table.Tr key={`${r.result}:${r.reason}`}>
                <Table.Td w={70} ta="right" style={FIG}>
                  {formatNumber(r.count)}
                </Table.Td>
                <Table.Td>
                  {/* The engine writes this string and it already reads as an
                      explanation, so it is shown as-is rather than re-worded
                      here: a re-wording would be another stale copy. */}
                  <Text size="xs" c="dimmed">{r.reason}</Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
}

function Destinations({
  group,
  onEdit,
  money,
}: {
  group: RouteGroup;
  onEdit: (d: GroupDestination) => void;
  /** Price and caps: shown only on a stream that sells. */
  money: boolean;
}) {
  const v = useVocab();
  const dests = group.destinations as GroupDestination[];
  if (dests.length === 0) {
    return (
      <Text size="sm" c="dimmed" mt="md">
        {`No ${v.many('destination')} linked to this ${v.one('routeGroup')}, so it can never deliver.`}
      </Text>
    );
  }

  const weighted = group.distribution_method === 'weighted';
  const totalWeight = weighted ? dests.reduce((n, d) => n + (d.weight || 0), 0) : 0;
  // In priority mode the trial order is the thing to see, so sort by it.
  const ordered =
    group.distribution_method === 'priority'
      ? [...dests].sort((a, b) => a.priority - b.priority)
      : dests;

  return (
    <Table.ScrollContainer minWidth={880}>
      <Table mt="md">
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={44}>{group.distribution_method === 'priority' ? '#' : ''}</Table.Th>
            <Table.Th>{v.One('destination')}</Table.Th>
            <Table.Th>{v.One('buyer')}</Table.Th>
            <Table.Th>Method</Table.Th>
            {weighted && <Table.Th>Share</Table.Th>}
            {money && <Table.Th ta="right">Price</Table.Th>}
            {money && <Table.Th pl={28}>Daily cap</Table.Th>}
            <Table.Th>Status</Table.Th>
            <Table.Th w={60} />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {ordered.map((d, idx) => (
            <Table.Tr key={d.link_id} opacity={d.active ? 1 : 0.55}>
              <Table.Td>
                {group.distribution_method === 'priority' && (
                  <span style={{ ...FIG, color: TOKENS.textMuted }}>{idx + 1}</span>
                )}
              </Table.Td>
              <Table.Td>
                <Text size="sm" fw={500}>{d.name}</Text>
                <Text size="xs" c="dimmed" lineClamp={1}>{d.endpoint_url || `no ${v.p.urlLower}`}</Text>
              </Table.Td>
              <Table.Td><Text size="sm">{d.buyer_name || d.buyer_id || '—'}</Text></Table.Td>
              <Table.Td>
                <Badge size="sm" variant="outline" color="gray">
                  {d.delivery_method || d.method}
                </Badge>
              </Table.Td>
              {weighted && (
                <Table.Td>
                  <Stack gap={2} miw={80}>
                    <span style={{ ...FIG, fontSize: 11.5 }}>
                      {totalWeight > 0 ? `${Math.round(((d.weight || 0) / totalWeight) * 100)}%` : '—'}
                    </span>
                    <Progress
                      value={totalWeight > 0 ? ((d.weight || 0) / totalWeight) * 100 : 0}
                      size={4}
                      radius={4}
                      bg={TOKENS.surface2}
                      color={CHART_INK_COLOUR}
                    />
                  </Stack>
                </Table.Td>
              )}
              {money && <Table.Td><PriceCell price={d.price as Price} /></Table.Td>}
              {money && (
                <Table.Td pl={28}><CapCell used={d.effective_caps.daily_count} cap={d.effective_caps.daily_cap} /></Table.Td>
              )}
              <Table.Td>
                <Badge variant="light" color={d.active ? 'chartGreen' : 'gray'}>
                  {d.active ? 'Active' : 'Paused'}
                </Badge>
              </Table.Td>
              <Table.Td>
                <Tooltip label={money ? "Edit this link's price, caps and filters" : "Edit this link's filters and schedule"}>
                  <ActionIcon variant="subtle" color="gray" onClick={() => onEdit(d)} aria-label={`Edit ${d.name}`}>
                    <IconPencil size={15} />
                  </ActionIcon>
                </Tooltip>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

export function CampaignDeliveryTab({
  campaignId,
  groups,
  money = true,
}: {
  campaignId: string | undefined;
  groups: RouteGroup[];
  /**
   * Whether this stream sells (a selling route, on or paused). Without one
   * there is no money here: no prices, caps or legacy price warnings.
   */
  money?: boolean;
}) {
  const [range, setRange] = useState<RoutingRange>('7d');
  const [editing, setEditing] = useState<RouteGroup | null>(null);
  const [editingLink, setEditingLink] = useState<
    { group: RouteGroup; destination: GroupDestination } | null
  >(null);
  const [addingGroup, setAddingGroup] = useState(false);
  const [linkingTo, setLinkingTo] = useState<RouteGroup | null>(null);
  const [addingRotation, setAddingRotation] = useState(false);
  const { data: routing, isPending, error } = useCampaignRouting(campaignId, range);
  const v = useVocab();
  const perms = useCrmPermissions();

  const stats = new Map((routing?.groups ?? []).map((g) => [g.group_id, g]));
  // Selling tiers in priority order; rotations after them, as the engine runs them.
  const tiers = [...groups].filter((g) => g.purpose !== 'assign').sort((a, b) => a.priority - b.priority);
  const rotations = perms.owners
    ? [...groups].filter((g) => g.purpose === 'assign').sort((a, b) => a.priority - b.priority)
    : [];

  const legacyPriced = tiers.flatMap((g) =>
    (g.destinations as GroupDestination[]).filter(
      (d) => (d.price as Price).source === 'buyer_settings',
    ),
  );

  return (
    <Stack>
      <Group justify="space-between" align="flex-end">
        <div>
          <MonoLabel>Outcomes</MonoLabel>
          <Text size="sm" c="dimmed" mt={2}>
            {isPending
              ? 'Loading what actually happened…'
              : routing
                ? `${routing.total_attempts.toLocaleString()} routing attempts in this period.`
                : 'Outcome data unavailable.'}
          </Text>
        </div>
        <Group gap="sm">
        <Button size="compact-sm" variant="default" leftSection={<IconPlus size={14} />} onClick={() => setAddingGroup(true)}>
          {`New ${v.one('routeGroup')}`}
        </Button>
        {perms.manageTeams && (
          <Button size="compact-sm" variant="default" leftSection={<IconUsersGroup size={14} />} onClick={() => setAddingRotation(true)}>
            Add assignment tier
          </Button>
        )}
        <MonoSegmented
          value={range}
          aria-label="Period"
          onChange={(v) => setRange(v as RoutingRange)}
          data={[
            { label: 'Today', value: 'today' },
            { label: '7 days', value: '7d' },
            { label: '30 days', value: '30d' },
            { label: '90 days', value: '90d' },
          ]}
        />
        </Group>
      </Group>

      {error && (
        <Alert color="chartAmber" variant="light" title="Outcome data unavailable">
          <Text size="sm">
            The routing configuration below is current; only the counts are missing. {error.message}
          </Text>
        </Alert>
      )}

      {money && legacyPriced.length > 0 && (
        <Alert color="chartAmber" variant="light" title={`Priced from the ${v.one('buyer')}'s settings blob`}>
          <Text size="sm">
            {`${v.count(legacyPriced.length, 'destination')} ${legacyPriced.length === 1 ? 'has' : 'have'} no price on the link or the ${v.one('destination')}, so the value is coming from `}
            <Code>{'buyers.settings.price'}</Code>
            {`. That is a legacy home being retired. Set a default on the ${v.one('destination')}, or an override on the link.`}
          </Text>
        </Alert>
      )}

      {routing && routing.pre_routing.length > 0 && (
        <Card withBorder padding="sm">
          <Group gap="lg" wrap="wrap">
            <Text size="sm" fw={600}>Screened before routing</Text>
            {routing.pre_routing.map((o, i) => (
              <LegendItem
                key={o.result}
                color={MONO_SCALE[Math.min(i + 2, MONO_SCALE.length - 1)] ?? TOKENS.textMuted}
                label={outcomeLabel(o.result).label}
                value={formatNumber(o.count)}
              />
            ))}
          </Group>
          <Text size="xs" c="dimmed" mt={6}>
            These never reached a tier. Duplicate and suppression checks run before routing starts.
          </Text>
        </Card>
      )}

      {tiers.length === 0 ? (
        <Card withBorder>
          <Text size="sm" c="dimmed">
            {rotations.length > 0
              ? `No ${v.one('routeGroup')} sells or sends this ${v.one('campaign')}'s ${v.many('lead')}. The rotation below gives them to reps.`
              : `This ${v.one('campaign')} has no ${v.many('route')} yet, so it only keeps what arrives. Add ${v.a('routeGroup')} to sell or send its ${v.many('lead')}, or an assignment tier to give them to reps.`}
          </Text>
        </Card>
      ) : (
        tiers.map((g, idx) => {
          const s = stats.get(g.id);
          const last = idx === tiers.length - 1;
          return (
            <div key={g.id}>
              <Card withBorder padding="md" opacity={g.active ? 1 : 0.6}>
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                  <Stack gap={4}>
                    <Group gap="sm" wrap="wrap">
                      <MonoLabel
                        style={{
                          color: g.active ? TOKENS.text : TOKENS.textMuted,
                          background: TOKENS.surface2,
                          border: `1px solid ${TOKENS.border}`,
                          borderRadius: 6,
                          padding: '1px 7px',
                        }}
                      >
                        Tier {g.priority}
                      </MonoLabel>
                      <Text fw={600}>{g.name}</Text>
                      <MonoLabel>{g.distribution_method}</MonoLabel>
                      {!g.active && <Badge size="sm" color="gray">disabled</Badge>}
                      {!g.filters_enabled &&
                        Array.isArray(g.smart_filters) &&
                        g.smart_filters.length > 0 && (
                          <Tooltip label={`This ${v.one('routeGroup')} has filters set up but they are switched off`}>
                            <Badge size="sm" color="chartAmber" variant="light">filters off</Badge>
                          </Tooltip>
                        )}
                    </Group>
                    <Text size="xs" c="dimmed">
                      {methodBlurb(g.distribution_method, g.destinations.length)}
                    </Text>
                  </Stack>
                  <Group gap="sm" align="flex-start" wrap="nowrap">
                    <Stack gap={4} miw={260}>
                      {isPending ? <Loader size="xs" /> : <TierStats stats={s} />}
                    </Stack>
                    <Button
                      size="compact-sm"
                      variant="default"
                      leftSection={<IconPencil size={14} />}
                      onClick={() => setEditing(g)}
                    >
                      Edit
                    </Button>
                  </Group>
                </Group>

                <TierReasons stats={s} />
                <Destinations group={g} money={money} onEdit={(d) => setEditingLink({ group: g, destination: d })} />
                <Group justify="flex-end" mt="xs">
                  <Button
                    size="compact-xs"
                    variant="subtle"
                    leftSection={<IconPlus size={13} />}
                    onClick={() => setLinkingTo(g)}
                  >
                    {`Add ${v.one('destination')}`}
                  </Button>
                </Group>
              </Card>

              {(!last || rotations.length > 0) && (
                <Group justify="center" gap={6} py="xs" c="dimmed">
                  <IconArrowDown size={14} />
                  <MonoLabel>
                    {last
                      ? `Unsold ${v.many('lead')} go to the assignment tier`
                      : s && s.turned_away > 0
                        ? `${formatNumber(s.turned_away)} moved on to tier ${tiers[idx + 1]?.priority}`
                        : `Falls through to tier ${tiers[idx + 1]?.priority}`}
                  </MonoLabel>
                </Group>
              )}
            </div>
          );
        })
      )}

      {rotations.map((g, i) => (
        <div key={g.id}>
          {i > 0 && (
            <Group justify="center" gap={6} py="xs" c="dimmed">
              <IconArrowDown size={14} />
              <MonoLabel>Not needed once one rotation has assigned</MonoLabel>
            </Group>
          )}
          <AssignmentTierCard group={g} canEdit={perms.manageTeams} />
        </div>
      ))}
      <NewRotationModal opened={addingRotation} onClose={() => setAddingRotation(false)} campaignId={campaignId} />

      <NewGroupModal
        campaignId={campaignId}
        opened={addingGroup}
        existing={tiers}
        onClose={() => setAddingGroup(false)}
      />
      <LinkDestinationModal campaignId={campaignId} group={linkingTo} onClose={() => setLinkingTo(null)} />
      <EditGroupModal campaignId={campaignId} group={editing} onClose={() => setEditing(null)} />
      <EditDestinationModal
        campaignId={campaignId}
        group={editingLink?.group ?? null}
        destination={editingLink?.destination ?? null}
        onClose={() => setEditingLink(null)}
      />

      {tiers.length > 0 && (
        <Group gap={6} c="dimmed" justify="center" pt="xs">
          <IconAlertTriangle size={14} />
          <Text size="xs">
            {`${v.A('lead')} that no tier accepts is not delivered. Counts are attempts, not ${v.many('lead')}: one ${v.one('lead')} can be tried in several tiers.`}
          </Text>
        </Group>
      )}
    </Stack>
  );
}
